/**
 * The authoring toolbar, laid out as Google Docs lays it out.
 *
 * WHY IT EXISTS AT ALL: the editor shipped without one. The collaboration, the lifecycle gate, the
 * digest fence and the debounced save were built first and the authoring surface was a bare
 * ProseMirror box -- a plumbing demonstration rather than something somebody writes a procedure in.
 *
 * THE SHAPE IS COPIED ON PURPOSE. A person who writes procedures has used Word or Docs for twenty
 * years; the order of the groups, the boxed font size between its minus and plus, the pill, the
 * hairline between clusters are all muscle memory, and matching them is worth more than any
 * arrangement of our own that is merely defensible.
 *
 * WHAT IS NOT COPIED IS THE BUTTONS THAT WOULD DO NOTHING. Google's strip carries an image button,
 * a comment button and a format painter; two of those are features this product has decided not to
 * build yet and the third has nowhere to put the bytes. A toolbar that looks complete and answers
 * nothing when pressed is worse than a shorter one -- so every control here acts, and the ones that
 * are missing are missing visibly rather than present and dead.
 *
 * DISABLED, NOT HIDDEN, when the revision is closed. A released document still has a toolbar; its
 * controls simply do not respond, which says "this is a document you cannot edit" where an absent
 * toolbar says "this is not a document".
 */
import React from "react";
import { Box, Divider, IconButton, Menu, MenuItem, Select, Tooltip, Typography } from "@mui/material";

import ColourPicker from "./ColourPicker";
import Symbol from "./Symbol";
import { Grey3 } from "../../theme/styles";

/**
 * ROBOTO, WHICH IS WHAT GOOGLE DOCS USES FOR ITS CHROME.
 *
 * The app's own face is Poppins -- a display face, chosen for a product's pages. A word processor's
 * menus and toolbar are not pages: they are a dense strip of small labels read at a glance all day,
 * and Roboto is drawn for exactly that. Already loaded by index.html, so it costs nothing.
 *
 * THE DOCUMENT ITSELF IS NOT AFFECTED -- that is Arial at 11pt, set by DocumentPaper, because the
 * page is the customer's document and not our interface.
 */
export const CHROME_FONT = "Roboto, 'Helvetica Neue', Arial, sans-serif";

/** Docs' own toolbar ink and tints, read off the product rather than invented. */
export const CHROME_INK = "#444746";
export const CHROME_SELECTED = "#C2E7FF";
export const CHROME_HOVER = "#E9EEF6";
export const CHROME_SURFACE = "#EDF2FA";

/** The faces a controlled document is actually written in. Not a font picker for its own sake. */
const FONTS = ["Arial", "Helvetica", "Times New Roman", "Georgia", "Courier New", "Verdana"];

/** Point sizes, as a document person says them. */
export const SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36];

/** The weights the border menu offers, the same set the Table options panel uses. */
const BORDER_WEIGHTS = [0, 0.5, 0.75, 1, 1.5, 2, 3];

/** What the page is magnified to. A view setting; it changes nothing in the document. */
export const ZOOMS = [50, 75, 90, 100, 125, 150, 200];

/**
 * THE INK AND HIGHLIGHT PALETTES ARE SMALL AND FIXED.
 *
 * A full colour wheel in a controlled document invites a rainbow nobody can print legibly and no
 * reviewer can interpret -- and colour that carries meaning in a procedure belongs in the words.
 * These are the ones people reach for: black for the body, a red and an amber for a caution, a
 * green and a blue for a reference, and highlights that survive photocopying.
 */
const INKS = ["#000000", "#525968", "#C0392B", "#8A5B00", "#1E9E5A", "#1498D8"];
const HIGHLIGHTS = ["#FFF59D", "#C8E6C9", "#B3E5FC", "#FFCCBC"];

/** The four alignments, hoisted out of the JSX: `as const` inside a child expression does not parse. */
const ALIGNMENTS: [string, string, string][] = [
    ["left", "format_align_left", "Left"],
    ["center", "format_align_center", "Centre"],
    ["right", "format_align_right", "Right"],
    ["justify", "format_align_justify", "Justify"],
];

/** Line spacing, in the increments a word processor offers. */
const SPACINGS: [string, string][] = [["1", "Single"], ["1.15", "1.15"], ["1.5", "1.5"], ["2", "Double"]];

type Props = {
    editor: any;
    disabled: boolean;
    mode: "editing" | "suggesting" | "viewing";
    onMode: (mode: "editing" | "suggesting") => void;
    outstanding: number;
    zoom: number;
    onZoom: (zoom: number) => void;
    onFind: () => void;
    collapsed: boolean;
    onCollapse: (collapsed: boolean) => void;
    /** The cell border the person has clicked, if any. The toolbar becomes about it. */
    border?: { edge: string; width: number; colour: string; dash: "solid" | "dashed" | "dotted" } | null;
    onBorder?: (width: number, colour: string, dash: "solid" | "dashed" | "dotted") => void;
};

/** A 28px round button, which is the single most Docs-like thing about the strip. */
function Bar({ title, active, disabled, onClick, children }: {
    title: string; active?: boolean; disabled: boolean;
    // THE EVENT IS PASSED ON, because a control that opens a menu needs the element it was clicked
    // on as the anchor -- and without it every such button has to be wrapped in something else.
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    children: React.ReactNode;
}) {
    return (
        <Tooltip title={title}>
            {/* A DISABLED CONTROL CANNOT BE HOVERED, so the tooltip needs an element that can --
                without the span the whole strip goes silent exactly when somebody is most likely
                to be asking why nothing responds. */}
            <span>
                <IconButton size="small" disabled={disabled} onClick={onClick} aria-label={title}
                            sx={{ width: 28, height: 28, borderRadius: "50%", color: CHROME_INK,
                                  backgroundColor: active ? CHROME_SELECTED : "transparent",
                                  "&:hover": { backgroundColor: active ? CHROME_SELECTED : CHROME_HOVER } }}>
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );
}

/** A button with a caret, for the controls that open a menu. */
function BarMenu({ title, disabled, icon, children }: {
    title: string; disabled: boolean; icon: React.ReactNode;
    children: (close: () => void) => React.ReactNode;
}) {
    const [open, setOpen] = React.useState<HTMLElement | null>(null);
    return (
        <>
            <Tooltip title={title}>
                <span>
                    <Box component="button" type="button" disabled={disabled} aria-label={title}
                         onClick={(e: any) => setOpen(e.currentTarget)}
                         sx={{ display: "flex", alignItems: "center", gap: "1px", height: 28, px: "4px",
                               border: "none", background: "transparent", borderRadius: "14px",
                               color: disabled ? Grey3 : CHROME_INK,
                               cursor: disabled ? "default" : "pointer",
                               "&:hover": { backgroundColor: disabled ? "transparent" : CHROME_HOVER } }}>
                        {icon}
                        <Symbol name="arrow_drop_down" size={16}/>
                    </Box>
                </span>
            </Tooltip>
            <Menu anchorEl={open} open={!!open} onClose={() => setOpen(null)}
                  slotProps={{ list: { dense: true, sx: { minWidth: 180, fontFamily: CHROME_FONT } } }}>
                {children(() => setOpen(null))}
            </Menu>
        </>
    );
}

function Swatches({ colours, onPick, title, icon, disabled }: {
    colours: string[]; onPick: (colour: string) => void; title: string;
    icon: React.ReactNode; disabled: boolean;
}) {
    const [open, setOpen] = React.useState<HTMLElement | null>(null);
    return (
        <>
            <Tooltip title={title}>
                <span>
                    <IconButton size="small" disabled={disabled} aria-label={title}
                                onClick={(e) => setOpen(e.currentTarget)}
                                sx={{ width: 28, height: 28, borderRadius: "50%", color: CHROME_INK,
                                      "&:hover": { backgroundColor: CHROME_HOVER } }}>
                        {icon}
                    </IconButton>
                </span>
            </Tooltip>
            <Menu anchorEl={open} open={!!open} onClose={() => setOpen(null)}
                  slotProps={{ list: { sx: { display: "flex", gap: 0.5, p: 0.75 } } }}>
                {colours.map(colour => (
                    <Box key={colour} role="button" aria-label={`${title} ${colour}`}
                         onClick={() => { onPick(colour); setOpen(null); }}
                         sx={{ width: 20, height: 20, borderRadius: "3px", cursor: "pointer",
                               backgroundColor: colour, border: `1px solid ${Grey3}` }}/>
                ))}
                <Box role="button" aria-label={`${title} none`}
                     onClick={() => { onPick(""); setOpen(null); }}
                     sx={{ width: 20, height: 20, borderRadius: "3px", cursor: "pointer", fontSize: 11,
                           border: `1px solid ${Grey3}`, display: "flex",
                           alignItems: "center", justifyContent: "center" }}>✕</Box>
            </Menu>
        </>
    );
}

const Line = () => <Divider orientation="vertical" flexItem sx={{ mx: "6px", my: "6px" }}/>;

export default function EditorToolbar({ editor, disabled, mode, onMode, outstanding,
                                        zoom, onZoom, onFind, collapsed, onCollapse,
                                        border, onBorder = () => {} }: Props) {
    const [borderWeight, setBorderWeight] = React.useState<HTMLElement | null>(null);
    const [borderDash, setBorderDash] = React.useState<HTMLElement | null>(null);
    const [modeMenu, setModeMenu] = React.useState<HTMLElement | null>(null);
    const [more, setMore] = React.useState<HTMLElement | null>(null);

    // RE-RENDERED ON EVERY SELECTION CHANGE, which is what makes B look pressed inside bold text.
    // Without it the toolbar is a set of buttons that act but never reflect, and a reader cannot
    // tell what the cursor is standing in.
    const [, bump] = React.useReducer((n: number) => n + 1, 0);
    React.useEffect(() => {
        if (!editor) return;
        editor.on("selectionUpdate", bump);
        editor.on("transaction", bump);
        return () => { editor.off("selectionUpdate", bump); editor.off("transaction", bump); };
    }, [editor]);

    if (!editor) return null;

    const chain = () => editor.chain().focus();
    const style = editor.isActive("heading", { level: 1 }) ? "h1"
                : editor.isActive("heading", { level: 2 }) ? "h2"
                : editor.isActive("heading", { level: 3 }) ? "h3"
                : "p";

    const setStyle = (value: string) => {
        if (value === "p") chain().setParagraph().run();
        else chain().toggleHeading({ level: Number(value.slice(1)) }).run();
    };

    const currentSize = Number(String(editor.getAttributes("textStyle").fontSize ?? "11pt")
                                   .replace(/[^\d.]/g, "")) || 11;
    const stepSize = (by: number) => {
        const next = Math.min(96, Math.max(6, currentSize + by));
        chain().setFontSize(`${next}pt`).run();
    };

    const selectSx = {
        fontSize: 14, color: "#1F1F1F", fontFamily: CHROME_FONT, height: 28,
        "&:before, &:after": { display: "none" },
        "& .MuiSelect-select": { py: 0, pl: 1 },
        "&:hover": { backgroundColor: CHROME_HOVER, borderRadius: "14px" },
    };

    // COLLAPSED STILL SHOWS THE CHEVRON, because a toolbar that vanishes with no way back is a
    // feature nobody uses twice.
    if (collapsed) {
        return (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Bar title="Show the toolbar" disabled={false} onClick={() => onCollapse(false)}>
                    <Symbol name="keyboard_arrow_down"/>
                </Bar>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "1px",
                   px: "6px", py: "4px", backgroundColor: CHROME_SURFACE, borderRadius: 999,
                   mb: 1, position: "sticky", top: 8, zIndex: 5, fontFamily: CHROME_FONT,
                   "& .MuiSelect-select, & .MuiMenuItem-root": { fontFamily: CHROME_FONT } }}>

            <Bar title="Find and replace" disabled={false} onClick={onFind}><Symbol name="search"/></Bar>
            <Bar title="Undo" disabled={disabled} onClick={() => chain().undo().run()}><Symbol name="undo"/></Bar>
            <Bar title="Redo" disabled={disabled} onClick={() => chain().redo().run()}><Symbol name="redo"/></Bar>
            <Bar title="Print" disabled={false} onClick={() => window.print()}><Symbol name="print"/></Bar>
            <Bar title="Clear formatting" disabled={disabled}
                 onClick={() => chain().unsetAllMarks().clearNodes().run()}><Symbol name="format_clear"/></Bar>

            {/* ZOOM IS A VIEW SETTING and changes nothing in the document -- which is why it sits
                with the view controls and not with the formatting. */}
            <Select value={zoom} disabled={false} variant="standard" disableUnderline
                    onChange={(e) => onZoom(Number(e.target.value))}
                    inputProps={{ "aria-label": "Zoom" }}
                    sx={{ ...selectSx, minWidth: 74 }}>
                {ZOOMS.map(z => <MenuItem key={z} value={z} sx={{ fontSize: 14 }}>{z}%</MenuItem>)}
            </Select>
            <Line/>

            {/* A BORDER IS SELECTED, SO THE TOOLBAR IS ABOUT THE BORDER. Google swaps the text
                controls out entirely, and that is the right call rather than a flourish: the font,
                the size and the alignment cannot act on a rule, so leaving them there offers twelve
                controls that do nothing and hides the three that do. Click away from the border and
                the ordinary toolbar comes back. */}
            {border ? (
                <>
                    <ColourPicker label="Border colour" disabled={disabled}
                                  value={border.colour}
                                  onChange={(next) => onBorder(border.width, next || "#000000",
                                                              border.dash)}/>
                    <Bar title={`Border weight — ${border.width} pt`} disabled={disabled}
                         onClick={(e: any) => setBorderWeight(e.currentTarget)}>
                        <Symbol name="line_weight"/>
                    </Bar>
                    <Bar title={`Border dash — ${border.dash}`} disabled={disabled}
                         onClick={(e: any) => setBorderDash(e.currentTarget)}>
                        <Symbol name="line_style"/>
                    </Bar>
                    <Menu anchorEl={borderWeight} open={!!borderWeight} onClose={() => setBorderWeight(null)}>
                        {BORDER_WEIGHTS.map(weight => (
                            <MenuItem key={weight} selected={weight === border.width} sx={{ fontSize: 14 }}
                                      onClick={() => { setBorderWeight(null);
                                                       onBorder(weight, border.colour, border.dash); }}>
                                {weight === 0 ? "None" : `${weight} pt`}
                            </MenuItem>
                        ))}
                    </Menu>
                    <Menu anchorEl={borderDash} open={!!borderDash} onClose={() => setBorderDash(null)}>
                        {(["solid", "dashed", "dotted"] as const).map(dash => (
                            <MenuItem key={dash} selected={dash === border.dash} sx={{ fontSize: 14 }}
                                      onClick={() => { setBorderDash(null);
                                                       onBorder(border.width, border.colour, dash); }}>
                                <Box sx={{ width: 64, borderTop: `2px ${dash} ${CHROME_INK}`, mr: 1.5 }}/>
                                {dash[0].toUpperCase() + dash.slice(1)}
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            ) : (<>

            <Select value={style} disabled={disabled} variant="standard" disableUnderline
                    onChange={(e) => setStyle(String(e.target.value))}
                    inputProps={{ "aria-label": "Paragraph style" }}
                    sx={{ ...selectSx, minWidth: 116 }}>
                <MenuItem value="p" sx={{ fontSize: 14 }}>Normal text</MenuItem>
                <MenuItem value="h1" sx={{ fontSize: 20, fontWeight: 600 }}>Heading 1</MenuItem>
                <MenuItem value="h2" sx={{ fontSize: 17, fontWeight: 600 }}>Heading 2</MenuItem>
                <MenuItem value="h3" sx={{ fontSize: 15, fontWeight: 600 }}>Heading 3</MenuItem>
            </Select>
            <Line/>

            <Select disabled={disabled} variant="standard" disableUnderline
                    value={editor.getAttributes("textStyle").fontFamily ?? "Arial"}
                    onChange={(e) => chain().setFontFamily(String(e.target.value)).run()}
                    inputProps={{ "aria-label": "Font" }}
                    sx={{ ...selectSx, minWidth: 128 }}>
                {FONTS.map(font => (
                    <MenuItem key={font} value={font} sx={{ fontSize: 14, fontFamily: font }}>{font}</MenuItem>
                ))}
            </Select>
            <Line/>

            {/* THE SIZE IS BOXED BETWEEN ITS MINUS AND PLUS, as every word processor draws it --
                the number is a field you can also pick from, not a label between two buttons. */}
            <Bar title="Smaller" disabled={disabled} onClick={() => stepSize(-1)}><Symbol name="remove" size={18}/></Bar>
            <Select disabled={disabled} variant="standard" disableUnderline
                    value={SIZES.includes(currentSize) ? currentSize : 11}
                    onChange={(e) => chain().setFontSize(`${e.target.value}pt`).run()}
                    inputProps={{ "aria-label": "Font size" }}
                    sx={{ ...selectSx, width: 46, border: `1px solid ${Grey3}`, borderRadius: "4px",
                          "& .MuiSelect-select": { py: 0, pl: 0, textAlign: "center" },
                          "& .MuiSelect-icon": { display: "none" },
                          "&:hover": { backgroundColor: CHROME_HOVER, borderRadius: "4px" } }}>
                {SIZES.map(size => <MenuItem key={size} value={size} sx={{ fontSize: 14 }}>{size}</MenuItem>)}
            </Select>
            <Bar title="Larger" disabled={disabled} onClick={() => stepSize(1)}><Symbol name="add" size={18}/></Bar>
            <Line/>

            <Bar title="Bold" disabled={disabled} active={editor.isActive("bold")}
                 onClick={() => chain().toggleBold().run()}><Symbol name="format_bold"/></Bar>
            <Bar title="Italic" disabled={disabled} active={editor.isActive("italic")}
                 onClick={() => chain().toggleItalic().run()}><Symbol name="format_italic"/></Bar>
            <Bar title="Underline" disabled={disabled} active={editor.isActive("underline")}
                 onClick={() => chain().toggleUnderline().run()}><Symbol name="format_underlined"/></Bar>
            <Swatches title="Text colour" disabled={disabled} colours={INKS}
                      icon={<Symbol name="format_color_text"/>}
                      onPick={(c) => (c ? chain().setColor(c).run() : chain().unsetColor().run())}/>
            <Swatches title="Highlight" disabled={disabled} colours={HIGHLIGHTS}
                      icon={<Symbol name="ink_highlighter"/>}
                      onPick={(c) => (c ? chain().setHighlight({ color: c }).run()
                                        : chain().unsetHighlight().run())}/>
            <Line/>

            <Bar title="Link" disabled={disabled} active={editor.isActive("link")}
                 onClick={() => {
                     const existing = editor.getAttributes("link").href ?? "";
                     // eslint-disable-next-line no-alert
                     const href = window.prompt("Link to", existing);
                     if (href === null) return;                 // cancelled: change nothing
                     if (href === "") chain().unsetLink().run();
                     else chain().setLink({ href }).run();
                 }}><Symbol name="link"/></Bar>
            <Bar title="Table" disabled={disabled}
                 onClick={() => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
                <Symbol name="table"/></Bar>
            <Bar title="Horizontal rule" disabled={disabled}
                 onClick={() => chain().setHorizontalRule().run()}><Symbol name="horizontal_rule"/></Bar>
            <Line/>

            {/* CARETS ONLY WHERE THERE IS A MENU. Google puts one on the list buttons too, for
                marker styles this editor does not offer -- and a caret that opens nothing is a
                worse copy than no caret. */}
            <BarMenu title="Align" disabled={disabled} icon={<Symbol name="format_align_left"/>}>
                {(close) => ALIGNMENTS.map(([where, glyph, label]) => (
                    <MenuItem key={where} selected={editor.isActive({ textAlign: where })}
                              sx={{ fontSize: 14 }}
                              onClick={() => { close(); chain().setTextAlign(where).run(); }}>
                        <Symbol name={glyph} size={18} sx={{ mr: 1 }}/> {label}
                    </MenuItem>
                ))}
            </BarMenu>
            <BarMenu title="Line spacing" disabled={disabled} icon={<Symbol name="format_line_spacing"/>}>
                {(close) => SPACINGS.map(([value, label]) => (
                    <MenuItem key={value} sx={{ fontSize: 14 }}
                              onClick={() => { close(); chain().setLineHeight(value).run(); }}>
                        {label}
                    </MenuItem>
                ))}
            </BarMenu>
            <Bar title="Checklist" disabled={disabled} active={editor.isActive("taskList")}
                 onClick={() => chain().toggleTaskList().run()}><Symbol name="checklist"/></Bar>
            <Bar title="Bulleted list" disabled={disabled} active={editor.isActive("bulletList")}
                 onClick={() => chain().toggleBulletList().run()}><Symbol name="format_list_bulleted"/></Bar>
            <Bar title="Numbered list" disabled={disabled} active={editor.isActive("orderedList")}
                 onClick={() => chain().toggleOrderedList().run()}><Symbol name="format_list_numbered"/></Bar>

            <Bar title="More" disabled={disabled} onClick={(() => {}) as any}>
                <Box component="span" onClick={(e: any) => setMore(e.currentTarget)}
                     sx={{ display: "flex" }}><Symbol name="more_vert"/></Box>
            </Bar>
            <Menu anchorEl={more} open={!!more} onClose={() => setMore(null)}
                  slotProps={{ list: { dense: true, sx: { minWidth: 200, fontFamily: CHROME_FONT } } }}>
                <MenuItem sx={{ fontSize: 14 }} onClick={() => { setMore(null); chain().toggleStrike().run(); }}>
                    <Symbol name="strikethrough_s" size={18} sx={{ mr: 1 }}/> Strikethrough
                </MenuItem>
                <MenuItem sx={{ fontSize: 14 }} onClick={() => { setMore(null); chain().toggleBlockquote().run(); }}>
                    <Symbol name="format_quote" size={18} sx={{ mr: 1 }}/> Quote
                </MenuItem>
                <MenuItem sx={{ fontSize: 14 }} onClick={() => { setMore(null); chain().sinkListItem("listItem").run(); }}>
                    <Symbol name="format_indent_increase" size={18} sx={{ mr: 1 }}/> Increase indent
                </MenuItem>
                <MenuItem sx={{ fontSize: 14 }} onClick={() => { setMore(null); chain().liftListItem("listItem").run(); }}>
                    <Symbol name="format_indent_decrease" size={18} sx={{ mr: 1 }}/> Decrease indent
                </MenuItem>
            </Menu>
            </>)}

            <Box sx={{ flexGrow: 1 }}/>

            {/* THE MODE, WHERE GOOGLE PUTS IT -- far right, named rather than an icon alone.
                "Editing" and "Suggesting" are a decision about what your typing MEANS, and a reader
                who cannot see which one they are in will not find out until their changes behave
                unexpectedly. The outstanding count rides on it because that is the number that
                blocks submission. */}
            <Tooltip title={mode === "suggesting"
                ? "Your changes become proposals for somebody to accept or reject"
                : "Your changes go straight into the document"}>
                <span>
                    <Box component="button" type="button" disabled={disabled} aria-label="Editing mode"
                         onClick={(e: any) => setModeMenu(e.currentTarget)}
                         sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, height: 28,
                               borderRadius: 999, border: "none", fontFamily: CHROME_FONT, fontSize: 13,
                               color: disabled ? Grey3 : CHROME_INK,
                               cursor: disabled ? "default" : "pointer",
                               backgroundColor: mode === "suggesting" ? "#FEF7E0" : "transparent",
                               "&:hover": { backgroundColor: disabled ? "transparent" : CHROME_HOVER } }}>
                        <Symbol name={mode === "suggesting" ? "rate_review" : "edit"} size={18}/>
                        <Typography component="span" sx={{ fontSize: 13, fontFamily: CHROME_FONT }}>
                            {mode === "suggesting" ? "Suggesting" : "Editing"}
                            {outstanding > 0 ? ` · ${outstanding}` : ""}
                        </Typography>
                        <Symbol name="arrow_drop_down" size={18}/>
                    </Box>
                </span>
            </Tooltip>
            <Menu anchorEl={modeMenu} open={!!modeMenu} onClose={() => setModeMenu(null)}
                  slotProps={{ list: { dense: true, sx: { minWidth: 280, fontFamily: CHROME_FONT } } }}>
                <MenuItem selected={mode === "editing"} sx={{ fontSize: 14 }}
                          onClick={() => { setModeMenu(null); onMode("editing"); }}>
                    <Symbol name="edit" size={18} sx={{ mr: 1 }}/> Editing — changes go straight in
                </MenuItem>
                <MenuItem selected={mode === "suggesting"} sx={{ fontSize: 14 }}
                          onClick={() => { setModeMenu(null); onMode("suggesting"); }}>
                    <Symbol name="rate_review" size={18} sx={{ mr: 1 }}/> Suggesting — changes become proposals
                </MenuItem>
            </Menu>

            <Line/>
            <Bar title="Hide the toolbar" disabled={false} onClick={() => onCollapse(true)}>
                <Symbol name="keyboard_arrow_up"/>
            </Bar>
        </Box>
    );
}

// DELIBERATELY ABSENT, so the gaps are decisions rather than oversights:
//
//   * IMAGES. There is nowhere to put the bytes yet. A controlled document's figures are evidence
//     and have to live where the attachments live, with the same retention -- a base64 blob inside
//     the body would bloat every save, and the collaboration transport caps an update at 128KB.
//   * COMMENTS, which Google puts beside the link button. Suggesting mode is the review path this
//     product has built; a second, parallel conversation layer is a product decision nobody has
//     taken yet.
//   * FORMAT PAINTER and SPELLING, which are the browser's or need a clipboard this cannot reach.
//   * PAGE HEADERS AND FOOTERS. Those belong to the RENDITION: the revision, the status and the
//     page count are facts QMS owns and that change without the text changing.
